import { Box, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { Sound, useGameSound } from '../../Sound';
import { gamePlayStore } from '../../store/GameplayStore';
import { GameplayAnswer } from '../../types/GameplayTypes';
import { useKeyPress } from '../../util/Keyboard';
import { muiStyleSheet } from '../../util/Styles';
import { FlipCard } from '../animation/FlipCard';

type Props = {
	rank: number;
	answer: GameplayAnswer;
};

export const AnswerCard: FunctionComponent<Props> = (props) => {
	const { rank, answer } = props;
	const dispatch = useDispatch();

	useKeyPress(String(rank), revealAnswer);

	function revealAnswer() {
		if (!answer.revealed) {
			dispatch(gamePlayStore.actions.answer({ answerId: rank - 1 }));
		}
	}

	useGameSound(Sound.effects.CorrectAnswer, props.answer.revealed);

	// Reduce text size for long answers that are likely to wrap
	const answerTextVariant = (answer.text.length < 25) ? 'h4' : 'h5';

	return <FlipCard
		flipped={answer.revealed}
		style={styles.flipCard}
		onClick={revealAnswer}
		aria-label={`Answer ${rank}`}

		front={/* Hidden side (purple with number) */
			<Typography variant="h2" sx={[styles.face, styles.rank]}>
				{rank}
			</Typography>}
		back={/* Revealed side (blue with answer text) */
			<Box sx={styles.face}>
				<Typography variant={answerTextVariant} sx={[styles.answerText, styles.answerContainer]}>
					{answer.text}
				</Typography>
				<Typography variant="h3" sx={[styles.answerText, styles.pointsContainer]}>
					{answer.points}
				</Typography>
			</Box>}
	/>;
};

const styles = muiStyleSheet({
	flipCard: {
		width: '100%',
		minWidth: '200px',
		height: '80px',
	},
	face: (theme) => ({
		width: '100%',
		height: '100%',
		display: 'flex',
		borderRadius: '8px',
		boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
		border: `4px solid ${theme.palette.game.gold}`,
	}),
	rank: (theme) => ({
		color: theme.palette.game.white,
		fontWeight: 'bold',
		fontSize: '3rem',
		alignItems: 'center',
		justifyContent: 'center',
		background: `linear-gradient(180deg, ${theme.palette.game.answerHidden.start} 0%, ${theme.palette.game.answerHidden.end} 100%)`,
	}),
	answerContainer: (theme) => ({
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingX: 2,
		background: `linear-gradient(180deg, ${theme.palette.game.answerRevealed.start} 0%, ${theme.palette.game.answerRevealed.end} 100%)`,
		borderRight: `2px solid ${theme.palette.game.white}`,
	}),
	answerText: (theme) => ({
		color: theme.palette.game.white,
		fontWeight: 'bold',
		textTransform: 'uppercase',
		textAlign: 'center',
	}),
	pointsContainer: (theme) => ({
		width: '90px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		bgcolor: theme.palette.game.answerRevealedDark,
	})
});